import { FunctionComponent, useContext, useEffect } from "react";

import ScrollableContainer from "../../layout/scrollable-container";
import Container from "../../layout/container";
import TransitionWrapper from "../../components/transition-wrapper/TransitionWrapper";

import SpecialistVector from "./assets/schedule-phone-call.png";
import SpecialistVectorWebp from "./assets/schedule-phone-call.webp";

import * as Styled from "./styles";
import Footer from "../../components/footer";
import { TransferContext } from "../../context/TransferContext";
import LoadingScreen from "../general/loading-screen";
import { BRIDGE_DEMO, BRIDGE_DEVELOPMENT, BRIDGE_PRODUCTION, BridgeEnv } from "../../typings";
import { AppContext } from "../../context/AppContext";
import useImageLoader from "../../utils/useLoadImage";
import Analytics from "../../utils/productAnalytics";
import { STEP_SCHEDULE_PHONE_CALL_CONFIRMATION } from "../../class/transferDetails";
import { NewTransferStatusType } from "../../class/transferInstruction";
import { BodyLargeRegular, BodyMediumRegular, HeadlineMedium } from "../../components/font";
import Flex from "../../layout/flex";
import { Button, tokens } from "@manifesthhqinc/ui-library";
import Spacing from "../../layout/spacing";

const getZohoBookingUrl = ({
  appEnv = BRIDGE_DEVELOPMENT,
  details,
}: {
  appEnv?: BridgeEnv;
  details: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    transferInstructionId: string;
    userId: string;
  };
}) => {
  const { firstName, lastName, phoneNumber, email, userId, transferInstructionId } = details;
  if (appEnv === BRIDGE_PRODUCTION && process.env.REACT_APP_ENV === "production") {
    return `https://manifest.zohobookings.com/portal-embed#/4630522000002738139?Name=${firstName} ${lastName}&Contact Number=${phoneNumber}&Email=${encodeURIComponent(email)}&userId=${userId}&transferInstructionId=${transferInstructionId}`;
  } else {
    return `https://manifest1.zohobookings.com/portal-embed#/4756981000000043054?Name=${firstName} ${lastName}&Contact Number=${phoneNumber}&Email=${encodeURIComponent(email)}&userId=${userId}&transferInstructionId=${transferInstructionId}`;
  }
};

interface SchedulePhoneCallScreenProps {
  shouldShowAnimation: boolean;
}

const SchedulePhoneCall: FunctionComponent<SchedulePhoneCallScreenProps> = ({
  shouldShowAnimation,
}: SchedulePhoneCallScreenProps) => {
  const {
    setShowCalendly,
    userDetails,
    isLoading,
    transferIntentDetails,
    transferInstructions,
    userId,
    userPhoneNumber,
    setTransferInstructions,
    goToScreen,
  } = useContext(TransferContext);
  const { appEnv } = useContext(AppContext);
  const { imageSrc, webpSrc } = useImageLoader({
    imgSrc: SpecialistVector,
    webp: SpecialistVectorWebp,
  });

  useEffect(() => {
    document.getElementById("zohoBooking")!.innerHTML = "";
    return () => {
      document.getElementById("zohoBooking")!.innerHTML = "";
    };
  }, []);

  const schedulePhoneCall = async () => {
    if (appEnv === BRIDGE_DEMO) {
      goToScreen(STEP_SCHEDULE_PHONE_CALL_CONFIRMATION);
      const updatedTransferInstructions = transferInstructions.map((e) => {
        if (e.transferInstructionStatus === NewTransferStatusType.SCHEDULE_PHONE_CALL) {
          return {
            ...e,
            transferInstructionStatus: NewTransferStatusType.PROCESSING,
            transferExternalStatus: "Processing",
            transferExternalStatusDescription: "Manifest is processing your transfer; no action is needed.",
          };
        }
        return e;
      });
      setTransferInstructions(updatedTransferInstructions);
    } else {
      Analytics.track({
        name: "Clicked Schedule on Schedule Phone call screen",
      });
      const zohoBookingDiv = document.getElementById("zohoBooking");
      zohoBookingDiv!.innerHTML = "";
      const iframe = document.createElement("iframe");
      iframe.src = getZohoBookingUrl({
        appEnv,
        details: {
          firstName: transferIntentDetails?.user?.firstName || userDetails.firstName || "",
          lastName: transferIntentDetails?.user?.lastName || userDetails.lastName || "",
          phoneNumber: transferIntentDetails?.userPhoneNumber
            ? transferIntentDetails?.userPhoneNumber
            : userPhoneNumber.startsWith("+")
              ? userPhoneNumber
              : `+${userPhoneNumber}` || "",
          email: transferIntentDetails?.userEmail || "",
          userId,
          transferInstructionId: transferInstructions
            .map((transferInstruction) => transferInstruction.transferInstructionId)
            .join(","),
        },
      });
      iframe.style.border = "none";
      zohoBookingDiv!.appendChild(iframe);
      setShowCalendly(true);
    }
  };

  if (isLoading || (!imageSrc && !webpSrc)) {
    return <LoadingScreen loadingScreenMessage="Saving..." />;
  }

  return (
    <TransitionWrapper>
      <ScrollableContainer name="phone-call-needed-screen">
        <Container>
          <Flex $flexGap={8} $flexDirection="column">
            <HeadlineMedium $textAlign="center">
              {!shouldShowAnimation ? (
                "Schedule a call"
              ) : (
                <>
                  When are you free <br />
                  for a call?
                </>
              )}
            </HeadlineMedium>
            <BodyLargeRegular $textAlign="center">
              Your Manifest transfer specialist will call you at the scheduled time with your provider on the line.{" "}
            </BodyLargeRegular>
          </Flex>
          <Styled.VectorWrapper>
            <img src={imageSrc} alt="assign specialist" height={173} />
          </Styled.VectorWrapper>
          <p className="form-description pr-20 pl-20" style={{ marginBottom: 0 }}></p>
        </Container>
      </ScrollableContainer>
      <Footer>
        <BodyMediumRegular $color={tokens.color.text.secondary} $textAlign="center">
          No preparation needed – we've got <br />
          everything covered!
        </BodyMediumRegular>
        <Spacing vertical="16" />
        <Button name="schedulePhoneCall" onClick={schedulePhoneCall} size="large">
          Schedule
        </Button>
      </Footer>
    </TransitionWrapper>
  );
};

export default SchedulePhoneCall;
